import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@wiggot/libs'
import { toast } from '@wiggui/ds/Toast'

export interface OptQueryParams {
  [key: string]: any
}

export interface IOpts {
  suspense?: boolean
  showToastError?: boolean
  initialData?: any
  enabled?: boolean
  retry?: number
  staleTime?: number
  gcTime?: number
  refetchOnWindowFocus?: boolean
}

export interface IUseGet<T> {
  data: T | undefined
  isError: boolean
  setData: (newData: T) => void
  isLoading: boolean
  status: 'error' | 'pending' | 'success'
  isFetching: boolean
  refetch: any
  isRefetching: boolean
  isSuccess: boolean
  isStale: boolean
  failureCount: number
}

export const useGet = <T = any>(config: {
  queryKey: string[]
  queryFn: (queryParams: OptQueryParams) => Promise<T>
  options?: IOpts
  queryParams?: OptQueryParams
  initialPageParams?: number
}): IUseGet<T> => {
  const { options, queryKey, queryFn, queryParams } = config
  const queryClient = useQueryClient()

  const {
    status,
    data,
    isFetching,
    isRefetching,
    refetch,
    isLoading,
    isError,
    isSuccess,
    isStale,
    failureCount
  } = useQuery<T>({
    queryKey,
    queryFn: () => queryFn(queryParams ?? {}),
    retry: options?.retry ?? 3,
    ...options
  })

  const setData = useCallback(
    (newData: T) => {
      queryClient.setQueryData(queryKey, newData)
    },
    [queryClient, queryKey]
  )

  if (isError && options?.showToastError) {
    toast({
      type: 'error',
      title: 'Error',
      description: 'Ha ocurrido un error'
    })
  }

  return {
    data,
    isError,
    setData,
    isLoading,
    status,
    isSuccess,
    isFetching,
    refetch,
    isRefetching,
    isStale,
    failureCount
  }
}
